import type {
  OperationCancellationReason,
  OperationDefinition,
  OperationGroup,
  OperationId,
  OperationKey,
  OperationResult,
} from './operation.types'

const IDENTIFIER_PATTERN = /^[a-z0-9][a-z0-9:._-]*$/

function validatedIdentifier(kind: string, value: string): string {
  if (value !== value.trim() || value.length === 0 || !IDENTIFIER_PATTERN.test(value)) {
    throw new Error(`Invalid ${kind}: ${JSON.stringify(value)}`)
  }
  return value
}

export function operationId(value: string): OperationId {
  return validatedIdentifier('OperationId', value) as OperationId
}

export function operationKey(value: string): OperationKey {
  return validatedIdentifier('OperationKey', value) as OperationKey
}

export function operationGroup(value: string): OperationGroup {
  return validatedIdentifier('OperationGroup', value) as OperationGroup
}

/**
 * Re-validates a key/group value produced by a definition callback at
 * execution time — the brand only proves it went through the constructor
 * once; callback output depends on arguments and is checked again (spec §8).
 */
export function assertValidCallbackOutput(kind: string, value: string): void {
  validatedIdentifier(kind, value)
}

/**
 * Freezes and statically validates a definition (spec §8).
 */
export function defineOperation<Args, Failure>(
  definition: OperationDefinition<Args, Failure>,
): Readonly<OperationDefinition<Args, Failure>> {
  if (definition.timeout.kind === 'after' && definition.timeout.milliseconds <= 0) {
    throw new Error(`defineOperation(${definition.id}): timeout milliseconds must be positive.`)
  }

  if (definition.retry.kind === 'bounded') {
    if (definition.retry.maxAttempts < 2) {
      throw new Error(`defineOperation(${definition.id}): bounded retry requires at least two attempts.`)
    }
    if (definition.safety.kind === 'non-idempotent-write') {
      throw new Error(`defineOperation(${definition.id}): retry is not allowed on a non-idempotent-write.`)
    }
  }

  if (definition.pending.kind === 'enqueue' && definition.pending.maxQueued <= 0) {
    throw new Error(`defineOperation(${definition.id}): enqueue requires a positive maxQueued.`)
  }

  if (definition.pending.kind === 'replace') {
    if (definition.safety.kind === 'non-idempotent-write') {
      throw new Error(`defineOperation(${definition.id}): replace is not allowed on a non-idempotent-write.`)
    }
    if (definition.safety.kind === 'idempotency-keyed-write') {
      throw new Error(`defineOperation(${definition.id}): replace is not allowed on an idempotency-keyed-write.`)
    }
  }

  if (definition.debounce.kind === 'trailing') {
    if (definition.debounce.milliseconds <= 0) {
      throw new Error(`defineOperation(${definition.id}): debounce milliseconds must be positive.`)
    }
    if (definition.pending.kind !== 'replace') {
      throw new Error(
        `defineOperation(${definition.id}): debounce requires pending: { kind: 'replace' } — a debounced call that is never superseded is a bug, not a policy.`,
      )
    }
    if (definition.safety.kind !== 'read') {
      throw new Error(`defineOperation(${definition.id}): debounce is not allowed on a write.`)
    }
  }

  return Object.freeze({
    id: definition.id,
    key: definition.key,
    groups: definition.groups,
    pending: Object.freeze({ ...definition.pending }),
    safety: Object.freeze({ ...definition.safety }),
    timeout: Object.freeze({ ...definition.timeout }),
    retry: Object.freeze({ ...definition.retry }),
    debounce: Object.freeze({ ...definition.debounce }),
  })
}

export function operationSucceeded<T>(value: T): Readonly<{ kind: 'succeeded'; value: T }> {
  return Object.freeze({ kind: 'succeeded' as const, value })
}

export function operationFailed<E>(error: E): Readonly<{ kind: 'failed'; error: E }> {
  return Object.freeze({ kind: 'failed' as const, error })
}

export function operationCancelled(
  reason: OperationCancellationReason,
): Readonly<{ kind: 'cancelled'; reason: OperationCancellationReason }> {
  return Object.freeze({ kind: 'cancelled' as const, reason })
}

export function operationSkipped(
  reason: 'already-pending' | 'queue-full',
): Readonly<{ kind: 'skipped'; reason: 'already-pending' | 'queue-full' }> {
  return Object.freeze({ kind: 'skipped' as const, reason })
}

export function discardOperationValue<T, E>(result: OperationResult<T, E>): OperationResult<null, E> {
  switch (result.kind) {
    case 'succeeded':
      return operationSucceeded(null)
    case 'failed':
      return operationFailed(result.error)
    case 'cancelled':
      return operationCancelled(result.reason)
    case 'skipped':
      return operationSkipped(result.reason)
  }
}
